// frontend/src/components/areas/SuccessMessage.tsx
import React, { useEffect } from 'react';
import { CheckCircle, X } from 'lucide-react';

interface SuccessMessageProps {
  message: string;
  onClose: () => void;
  autoClose?: boolean;
  duration?: number;
}

const SuccessMessage: React.FC<SuccessMessageProps> = ({ 
  message, 
  onClose, 
  autoClose = true, 
  duration = 5000 
}) => {
  useEffect(() => {
    if (autoClose) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [autoClose, duration, onClose]);

  return (
    <div className="fixed top-4 right-4 z-50 max-w-sm w-full">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
        {/* Success indicator bar */}
        <div className="h-1 bg-green-500"></div>
        
        <div className="p-4">
          <div className="flex items-start">
            {/* Success Icon */}
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center w-8 h-8 bg-green-100 rounded-full">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
            </div>

            {/* Message */}
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-900">
                Success!
              </p>
              <p className="text-sm text-gray-600 mt-1">
                {message}
              </p>
            </div>

            {/* Close Button */}
            <div className="ml-4 flex-shrink-0">
              <button
                onClick={onClose}
                className="inline-flex text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Progress bar for auto-close */}
        {autoClose && (
          <div className="h-1 bg-gray-100">
            <div 
              className="h-full bg-green-500 ease-linear"
              style={{
                width: '100%',
                transition: `width ${duration}ms linear`,
                animation: `shrinkWidth ${duration}ms linear forwards`
              }}
            />
          </div>
        )}
      </div>

      {/* Add keyframes animation via style tag */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes shrinkWidth {
            from { width: 100%; }
            to { width: 0%; }
          }
        `
      }} />
    </div>
  );
};

export default SuccessMessage;